import { generateUUID } from "../../../../utils/uuid";
import { UNINITIALISED_PROFILE } from "./consts";

/**
 * Retrieves the user's profile data from localStorage
 *
 * This function attempts to retrieve and parse the user's profile from localStorage.
 * It also ensures that all basket items have unique IDs by generating UUIDs for any
 * items missing them.
 *
 * @returns The user's profile object or null if no profile exists or an error occurs
 */
export function getOfflineProfile(): DogsTrust.Profile | null {
	try {
		if (typeof localStorage === "undefined") {
			return UNINITIALISED_PROFILE;
		}
		const profileString: string | null = localStorage.getItem("offlineProfile");
		if (profileString) {
			const parsedProfile: DogsTrust.Profile = JSON.parse(profileString);

			// Ensure all basket items have IDs
			const updatedBasket = (parsedProfile.basket || []).map((dog) => ({
				...dog,
				basketId: dog.basketId || generateUUID(),
			}));

			return {
				...parsedProfile,
				basket: updatedBasket,
			};
		}
	} catch (error) {
		console.error("🐛 Error getting offline profile:", error);
	}

	return UNINITIALISED_PROFILE;
}

/**
 * Removes the user's offline profile data from localStorage
 *
 * This function clears all stored offline profile data, effectively logging the user
 * out of offline mode.
 */
export function clearOfflineProfile() {
	localStorage.removeItem("offlineProfile");
}

/**
 * Updates the user's offline profile data in localStorage
 *
 * This function saves the provided profile to localStorage and adds a timestamp
 * to track when the profile was last synchronized locally. This is useful for
 * determining the freshness of offline data when reconnecting.
 *
 * @param profile - The user profile to save in localStorage
 */
export function updateOfflineProfile(profile: DogsTrust.Profile) {
	localStorage.setItem(
		"offlineProfile",
		JSON.stringify({
			...profile,
			localSyncTime: new Date().getTime(),
		}),
	);
}

export function confirmOfflineProfileHasMerged() {
	const offlineProfile = getOfflineProfile();
	if (offlineProfile) {
		localStorage.setItem(
			"offlineProfile",
			JSON.stringify({
				...offlineProfile,
				hasMerged: true,
			}),
		);
	}
}
